import { flatMap, isEmpty } from "lodash/fp";
import { DateTime } from "luxon";

import { SpeakerEvent } from "@app/analytics/MeetingReport/types";
import { TalkTurnData } from "@app/analytics/types";
import { BaseTranscriptTurn, MeetingReport } from "@app/common/types";
import { parseUTCDateTime } from "@app/utils/utils";

const parseEventFromTalkTurn = (
  turn: TalkTurnData,
  meetingStartTime: DateTime
): Omit<SpeakerEvent, "speaker"> => {
  const rawStartTime = parseUTCDateTime(turn.startTime);
  const rawEndTime = parseUTCDateTime(turn.endTime);

  return {
    rawStartTime,
    rawEndTime,
    startTime: rawStartTime.diff(meetingStartTime).as("seconds"),
    endTime: rawEndTime.diff(meetingStartTime).as("seconds"),
  };
};

const getSpeakerEventsFromReport = (
  meetingReport: MeetingReport,
  meetingStartTime: DateTime
): SpeakerEvent[] => {
  return flatMap(
    (person) =>
      person.talkTurns.map((turn) => ({
        speaker: person.name,
        ...parseEventFromTalkTurn(turn, meetingStartTime),
      })),
    meetingReport.talkTimeData
  );
};

const getSpeakerEventsFromTurns = (
  transcriptTurns: BaseTranscriptTurn[],
  meetingStartTime: DateTime
): SpeakerEvent[] =>
  transcriptTurns.map((turn) => ({
    rawStartTime: turn.rawStartTime,
    rawEndTime: turn.rawEndTime,
    startTime: turn.rawStartTime.diff(meetingStartTime).as("seconds"),
    endTime: turn.rawEndTime.diff(meetingStartTime).as("seconds"),
    speaker: turn.speaker,
  }));

export const getSpeakerEvents = (
  transcriptTurns: BaseTranscriptTurn[],
  meetingReport?: MeetingReport
): SpeakerEvent[] => {
  if (!meetingReport || !meetingReport.rawStartTime) return [];
  const meetingStartTime = DateTime.fromMillis(meetingReport.rawStartTime);

  if (isEmpty(transcriptTurns)) {
    return getSpeakerEventsFromReport(meetingReport, meetingStartTime);
  }

  return getSpeakerEventsFromTurns(transcriptTurns, meetingStartTime);
};

export const getNearestDataPoint = (data: any, index: number, x: number) => {
  if (index < 1) return data[0];
  if (index > data.length) return data[data.length - 1];
  const d0 = data[index - 1];
  const d1 = data[index];
  let dataPoint = d0;
  if (d1) {
    dataPoint = x - d0.x > d1.x - x ? d1 : d0;
  }
  return dataPoint;
};
